import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';

import type { WithChildren } from 'common/types';
import styles from './BottomSheet.module.scss';

interface IBottomSheetProps {
  title: string;
  // Optional hint rendered under the title, inside the header (not the scroll body).
  subtitle?: React.ReactNode;
  onClose: () => void;
  footer?: React.ReactNode;
  // Optional action rendered in the header, between the title and the close (X).
  headerAction?: React.ReactNode;
  fullScreen?: boolean;
  // Center the dialog on screen (all breakpoints) instead of pinning it to the
  // bottom. Footer buttons align to the right. Used for the kit pickers.
  centered?: boolean;
  // Widen the (centered) dialog for grid content like the companions catalog.
  wide?: boolean;
  closeDisabled?: boolean;
}

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Modal that renders as a centered card on desktop and a bottom-sheet on
 * mobile (mobile-first kit UI). Portals into #modal-root, locks body scroll,
 * traps Escape, and exposes a sticky footer slot for the primary CTA.
 */
const BottomSheet = ({
  title,
  subtitle,
  onClose,
  footer,
  headerAction,
  fullScreen = false,
  centered = false,
  wide = false,
  closeDisabled = false,
  children,
}: WithChildren<IBottomSheetProps>) => {
  const sheetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !closeDisabled) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose, closeDisabled]);

  // Focus management: move focus into the sheet on open, restore it to the
  // previously focused element on close, and trap Tab/Shift+Tab inside the sheet.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const sheet = sheetRef.current;

    const focusables = () => (sheet
      ? Array.from(sheet.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
        .filter((el) => el.offsetParent !== null || el === document.activeElement)
      : []);

    const first = focusables()[0];
    (first ?? sheet)?.focus();

    const onTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !sheet) return;
      const items = focusables();
      if (items.length === 0) {
        // Nothing focusable inside: keep focus on the container.
        e.preventDefault();
        sheet.focus();
        return;
      }
      const firstItem = items[0];
      const lastItem = items[items.length - 1];
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === firstItem || active === sheet) {
          e.preventDefault();
          lastItem.focus();
        }
      } else if (active === lastItem) {
        e.preventDefault();
        firstItem.focus();
      }
    };

    window.addEventListener('keydown', onTab);
    return () => {
      window.removeEventListener('keydown', onTab);
      previouslyFocused?.focus?.();
    };
  }, []);

  const root = document.getElementById('modal-root');
  if (!root) return null;

  return createPortal(
    <>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
      <div className={styles.backdrop} onClick={closeDisabled ? undefined : onClose} />
      <div
        ref={sheetRef}
        tabIndex={-1}
        className={classNames(styles.sheet, {
          [styles.fullScreen]: fullScreen,
          [styles.centered]: centered,
          [styles.wide]: wide,
        })}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className={styles.header}>
          <div className={styles.headerTitle}>
            <h2 className={styles.title}>{title}</h2>
            {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
          </div>
          {headerAction && <div className={styles.headerActions}>{headerAction}</div>}
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            disabled={closeDisabled}
            aria-label="Close"
          >
            &#x2715;
          </button>
        </div>
        <div className={styles.body}>{children}</div>
        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
    </>,
    root,
  );
};

export default BottomSheet;
